import {useState} from "react";
import {useElements, useStripe} from "@stripe/react-stripe-js";
import {useFunnel} from "../context/FunnelContext";
import {getStripeConfig} from "../config/stripe";
import {useNavigate} from "react-router-dom";
import {trackPayment} from "../utils/analytics";
import {trackFBEvent} from "../utils/facebookPixel";

export function useStripePayment() {
    const stripe = useStripe();
    const elements = useElements();
    const {state} = useFunnel();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const {apiUrl, couponId} = getStripeConfig();
    const navigate = useNavigate();

    const processPayment = async (priceId) => {
        if (!stripe || !elements) {
            throw new Error("Stripe has not been initialized");
        }

        const email = state.userData.email;
        if (!email) {
            throw new Error("Email is required");
        }

        setLoading(true);
        setError(null);

        try {
            const cardElement = elements.getElement("card");
            const {error: pmError, paymentMethod} =
                await stripe.createPaymentMethod({
                    type: "card",
                    card: cardElement,
                    billing_details: {email},
                });

            if (pmError) {
                throw pmError;
            }

            const response = await fetch(`${apiUrl}/create-subscription`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    email,
                    payment_method: paymentMethod.id,
                    coupon_id: couponId,
                    price_id: priceId,
                }),
            });

            const subscription = await response.json();

            console.log(subscription);

            if (subscription.error) {
                throw new Error(subscription.error.message);
            }

            if (subscription.clientSecret) {
                const {error: confirmError} = await stripe.confirmCardPayment(
                    subscription.clientSecret,
                );

                if (confirmError) {
                    throw confirmError;
                }
            }

            if (subscription.subscriptionId) {
                trackPayment(priceId, subscription.amount);
                trackFBEvent("Purchase", {
                    content_name: state.userData.selectedPlan.duration,
                    content_type: "Subscription",
                    currency: "USD",
                    value: subscription.amount,
                });
                navigate("/payment/success");
            }

            return subscription;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const processApplePayment = async (event, priceId) => {
        if (!stripe) {
            throw new Error("Stripe has not been initialized");
        }

        setLoading(true);
        setError(null);

        try {
            const email = event.payerEmail || state.userData.email;

            const response = await fetch(`${apiUrl}/create-subscription`, {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    email,
                    payment_method: event.paymentMethod.id,
                    coupon_id: couponId,
                    price_id: priceId,
                }),
            });

            const subscription = await response.json();

            if (subscription.error) {
                event.complete('fail');
                throw new Error(subscription.error.message);
            }

            if (subscription.clientSecret) {
                const {error: confirmError} = await stripe.confirmCardPayment(
                    subscription.clientSecret,
                    {payment_method: event.paymentMethod.id},
                    {handleActions: false}
                );

                if (confirmError) {
                    event.complete('fail');
                    throw confirmError;
                }
            }

            event.complete('success');

            if (subscription.subscriptionId) {
                trackPayment(priceId, subscription.amount);
                trackFBEvent("Purchase", {
                    content_name: state.userData.selectedPlan.duration,
                    content_type: "Subscription",
                    currency: "USD",
                    value: subscription.amount,
                });
                navigate("/payment/success");
            }

            return subscription;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        processPayment,
        processApplePayment,
        loading,
        error,
    };
}
